/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Card, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TableCell, TextField, IconButton, Chip, Tooltip, InputAdornment, Grid,
  Table, TableBody, TableContainer, TableRow, TablePagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../../../api';
import { formKeyHandler } from 'utils/formKeyboard';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';

const TEAL = '#00798E';
const TH = { py: 1.5, px: 3, textTransform: 'uppercase', fontSize: '0.625rem', fontWeight: 700, color: 'text.secondary', opacity: 0.7, borderBottom: '1.5px solid #f0f2f5', whiteSpace: 'nowrap', letterSpacing: '0.06em', bgcolor: 'transparent' };
const td = (last) => ({ py: 1.5, px: 3, borderBottom: last ? 'none' : '1px solid rgba(0,0,0,0.06)', fontSize: '0.8125rem' });
const upperSx = { '& .MuiInputBase-input': { textTransform: 'uppercase' } };
const EMPTY_FORM = { hsn_code: '', description: '', gst_percentage: '', cess_percentage: '0', hsn_level_discount_percentage: '' };

function HsnDialog({ open, onClose, onSaved, initial }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    setForm(initial ? {
      hsn_code: initial.hsn_code, description: initial.description,
      gst_percentage: String(initial.gst_percentage),
      cess_percentage: String(initial.cess_percentage || 0),
      hsn_level_discount_percentage: initial.hsn_level_discount_percentage !== null && initial.hsn_level_discount_percentage !== undefined
        ? String(initial.hsn_level_discount_percentage) : '',
    } : EMPTY_FORM);
  }, [initial, open]);

  const gst = parseFloat(form.gst_percentage) || 0;
  const half = (gst / 2).toFixed(2);

  const handleSave = async () => {
    if (!form.hsn_code.trim() || !form.description.trim() || form.gst_percentage === '') {
      Swal.fire('Validation', 'HSN Code, Description and GST % are required', 'warning'); return;
    }
    setSaving(true);
    try {
      const payload = {
        hsn_code: form.hsn_code, description: form.description,
        gst_percentage: parseFloat(form.gst_percentage),
        cgst_percentage: parseFloat(half), sgst_percentage: parseFloat(half),
        igst_percentage: parseFloat(form.gst_percentage),
        cess_percentage: parseFloat(form.cess_percentage) || 0,
        hsn_level_discount_percentage: form.hsn_level_discount_percentage !== '' ? parseFloat(form.hsn_level_discount_percentage) : null,
      };
      if (isEdit) await api.pharmacy.updateHsn(initial.id, payload);
      else await api.pharmacy.createHsn(payload);
      Swal.fire({ icon: 'success', title: isEdit ? 'Updated' : 'Created', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
      onSaved(); onClose();
    } catch { /* handled */ } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ color: TEAL, fontWeight: 700, borderBottom: '1px solid #f0f2f5', pb: 1.5 }}>
        {isEdit ? 'Edit HSN Code' : 'New HSN Code'}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }} onKeyDown={formKeyHandler(handleSave)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}><TextField autoFocus={!isEdit} fullWidth label="HSN Code *" value={form.hsn_code} onChange={e => setForm(p => ({ ...p, hsn_code: e.target.value }))} disabled={isEdit} /></Grid>
          <Grid item xs={12} sm={7}><TextField fullWidth label="Description *" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value.toUpperCase() }))} sx={upperSx} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="GST % *" type="number" value={form.gst_percentage} onChange={e => setForm(p => ({ ...p, gst_percentage: e.target.value }))} inputProps={{ min: 0, max: 100, step: 0.01 }} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="CGST %" value={half} disabled helperText="= GST / 2" /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="SGST %" value={half} disabled helperText="= GST / 2" /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Cess %" type="number" value={form.cess_percentage} onChange={e => setForm(p => ({ ...p, cess_percentage: e.target.value }))} inputProps={{ min: 0, step: 0.01 }} /></Grid>
          <Grid item xs={12} sm={8}><TextField fullWidth label="HSN Level Discount % (optional)" type="number" value={form.hsn_level_discount_percentage} onChange={e => setForm(p => ({ ...p, hsn_level_discount_percentage: e.target.value }))} inputProps={{ min: 0, max: 100, step: 0.01 }} helperText="Leave blank if no HSN-level discount" /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid #f0f2f5', gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>Cancel</MDButton>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ background: TEAL, color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, px: 3, boxShadow: 'none', '&:hover': { background: '#016476', boxShadow: 'none' } }}>
          {saving ? 'Saving…' : 'Save (Ctrl+↵)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function HsnPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const kb = useListKeyboard({
    rowCount: rows.length,
    enabled: !dialogOpen,
    onOpen: (i) => { if (rows[i]) { setEditing(rows[i]); setDialogOpen(true); } },
    onCreate: () => { setEditing(null); setDialogOpen(true); },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.pharmacy.listHsn({ search: search || undefined, limit: pageSize, offset: page * pageSize });
      setRows(res.data || []); setTotal(res.total || 0);
    } catch { /* handled */ } finally { setLoading(false); }
  }, [search, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleActive = async (row) => {
    try { await api.pharmacy.updateHsn(row.id, { is_active: row.is_active === 'Y' ? 'N' : 'Y' }); fetchData(); } catch { /* handled */ }
  };

  return (
    <Card>
      <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="logo" borderRadius="lg" coloredShadow="logo" display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="h6" color="white">HSN Codes &amp; GST</MDTypography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => { setEditing(null); setDialogOpen(true); }} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>New HSN Code (Alt+N)</Button>
      </MDBox>
      <MDBox p={2}>
        <TextField {...kb.searchProps} size="small" placeholder="Search HSN code or description…  ( / to focus )" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} sx={{ mb: 2, width: 300 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <TableContainer sx={{ boxShadow: 'none' }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={TH}>HSN Code</TableCell>
                <TableCell sx={TH}>Description</TableCell>
                <TableCell sx={TH}>GST %</TableCell>
                <TableCell sx={TH}>CGST %</TableCell>
                <TableCell sx={TH}>SGST %</TableCell>
                <TableCell sx={TH}>Cess %</TableCell>
                <TableCell sx={TH}>HSN Discount</TableCell>
                <TableCell sx={TH}>Status</TableCell>
                <TableCell sx={TH}>Actions</TableCell>
              </TableRow>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, border: 'none' }}><CircularProgress size={24} sx={{ color: TEAL }} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, border: 'none', color: 'text.secondary' }}>No HSN codes found</TableCell></TableRow>
              ) : rows.map((row, idx) => {
                const last = idx === rows.length - 1;
                return (
                  <TableRow key={row.id} hover {...kb.getRowProps(idx)} sx={{ '&:hover': { bgcolor: 'rgba(0,121,142,0.04)' }, ...(kb.activeIdx === idx ? ACTIVE_ROW_SX : {}) }}>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>{row.hsn_code}</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.description}</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.gst_percentage}%</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.cgst_percentage}%</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.sgst_percentage}%</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.cess_percentage}%</MDTypography></TableCell>
                    <TableCell sx={td(last)}>
                      {row.hsn_level_discount_percentage !== null && row.hsn_level_discount_percentage !== undefined && row.hsn_level_discount_percentage > 0
                        ? <Chip label={`${row.hsn_level_discount_percentage}%`} size="small" color="warning" />
                        : <MDTypography variant="caption" color="text" sx={{ opacity: 0.4 }}>—</MDTypography>}
                    </TableCell>
                    <TableCell sx={td(last)}>
                      <Chip label={row.is_active === 'Y' ? 'Active' : 'Inactive'} size="small" color={row.is_active === 'Y' ? 'success' : 'default'} onClick={() => handleToggleActive(row)} sx={{ cursor: 'pointer' }} />
                    </TableCell>
                    <TableCell sx={td(last)}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(row); setDialogOpen(true); }}><EditIcon fontSize="small" sx={{ color: TEAL }} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={pageSize} onRowsPerPageChange={e => { setPageSize(+e.target.value); setPage(0); }} rowsPerPageOptions={[10, 20, 50]} />
      </MDBox>
      <HsnDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={fetchData} initial={editing} />
    </Card>
  );
}

export default function PharmHsnCodes() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}><HsnPanel /></MDBox>
      <Footer />
    </DashboardLayout>
  );
}
